import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const categoryObj = categories.find((c) => c.slug === article.category);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: '孟若羽官方粉丝站',
    },
    publisher: {
      '@type': 'Organization',
      name: '孟若羽官方粉丝站',
    },
    image: article.imageUrl,
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <article className="w-full px-6 md:px-10 pt-24 pb-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link
                to={`/category/${article.category}`}
                className="hover:text-primary-600 transition-colors"
              >
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
              </div>
            </div>

            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-li:text-foreground-700 prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line text-lg"></i>
                </button>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200"
                  aria-label="复制链接"
                >
                  <i className="ri-link text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-6 md:px-10 py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard
                    key={related.slug}
                    slug={related.slug}
                    title={related.title}
                    excerpt={related.excerpt}
                    imageUrl={related.imageUrl}
                    date={related.date}
                    readTime={related.readTime}
                    categoryName={related.categoryName}
                    tags={related.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Footer />
    </>
  );
}